import {
  Box,
  CircularProgress,
  Dialog,
  Typography,
} from "@mui/material";
import {
  VisibilityOff as HideIcon,
  DeleteForever as ForceDeleteIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import { useEffect, useState } from "react";
import { RTL } from "../../cache/CacheRtl";
import { t } from "../dashboard/coach/coachTokens";
import { getMeasurementDefinitionImpact } from "../../services/MeasurementDefinitionService";

/** The Hebrew name of the input slot a blocked measurement fills, from the server's role code. */
const ROLE_LABELS = { WAIST: "היקף בטן", NECK: "היקף צוואר" };

/**
 * Delete dialog for a company measurement definition.
 *
 * On open, fetches the impact (how many recorded values / trainees). Then:
 *  - LOCKED (an input to a computed measurement) → neither action is possible; explain and stop.
 *  - no values → a single "hide" confirm.
 *  - has values → two choices: Hide (soft-delete, keep history, revivable) or
 *    Delete everything (force, wipe all values across all trainees, irreversible).
 *
 * onConfirm(force: boolean) is called with the chosen mode.
 *
 * The locked branch lives here, not only on the callers, because this dialog is opened from three
 * places — the catalog row, the measurement's own edit page, and "remove from the last gender" — and
 * only the first of those can see the lock on the row it is rendering.
 */
const DeleteMeasurementDefinitionDialog = ({ open, definition, companyId, onClose, onConfirm }) => {
  const [loading, setLoading] = useState(false);
  const [impact, setImpact] = useState({ valueCount: 0, traineeCount: 0, formulaUsage: [] });
  const [confirmingForce, setConfirmingForce] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && definition?.id) {
      setLoading(true);
      setConfirmingForce(false);
      getMeasurementDefinitionImpact(definition.id, companyId).then((res) => {
        setImpact(res.data || { valueCount: 0, traineeCount: 0, formulaUsage: [] });
        setLoading(false);
      });
    }
  }, [open, definition, companyId]);

  const handle = async (force) => {
    setBusy(true);
    await onConfirm(force);
    setBusy(false);
  };

  const hasValues = impact.valueCount > 0;
  const formulaUsage = impact.formulaUsage || [];
  const locked = formulaUsage.length > 0;

  const actionBtn = (label, color, bg, onClick, icon, disabled) => (
    <Box
      component="button"
      onClick={onClick}
      disabled={disabled}
      sx={{
        display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "6px",
        width: "100%", height: 42, borderRadius: t.rs,
        background: bg, color, border: `1px solid ${color}33`,
        fontSize: "14px", fontWeight: 600, fontFamily: "inherit",
        cursor: disabled ? "default" : "pointer", transition: "all .14s",
        "&:hover": disabled ? {} : { filter: "brightness(0.97)" },
        "&:disabled": { opacity: 0.6, cursor: "default" },
      }}
    >
      {icon}
      {label}
    </Box>
  );

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose}
            PaperProps={{ sx: { borderRadius: t.r, minWidth: { xs: "92%", sm: 440 }, padding: "6px" } }}>
      <RTL>
        <Box dir="rtl" sx={{ padding: "20px 22px" }}>
          <Typography sx={{ fontSize: 18, fontWeight: 800, color: t.text, mb: 0.5 }}>
            מחיקת מדד גוף
          </Typography>
          <Typography sx={{ fontSize: 14, color: t.t2, mb: 2 }}>
            {`"${definition?.name || ""}"`}
          </Typography>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={26} sx={{ color: t.green }} />
            </Box>
          ) : locked ? (
            <>
              <Box sx={{
                display: "flex", alignItems: "flex-start", gap: 1, mb: 2,
                background: t.redLight, borderRadius: t.rs, padding: "12px 14px",
              }}>
                <WarningIcon sx={{ fontSize: 20, color: t.red, mt: "1px" }} />
                <Box>
                  <Typography sx={{ fontSize: 13.5, color: t.redText, lineHeight: 1.5, fontWeight: 500 }}>
                    לא ניתן להסתיר או למחוק מדד זה: הוא משמש כמדידת מקור של מדד מחושב.
                  </Typography>
                  {formulaUsage.map((usage) => (
                    <Typography key={usage.formulaDefinitionId}
                                sx={{ fontSize: 13, color: t.redText, lineHeight: 1.6, mt: 0.75 }}>
                      {`• "${usage.formulaName}" — בשדה ${ROLE_LABELS[usage.role] || "מקור"}`}
                    </Typography>
                  ))}
                </Box>
              </Box>
              <Typography sx={{ fontSize: 13, color: t.t2, mb: 2, lineHeight: 1.6 }}>
                כדי להמשיך, נקו את בחירת המדידה בדף המדד המחושב, או הסתירו את המדד המחושב עצמו.
              </Typography>
              {actionBtn("סגור", t.t2, t.white, onClose, null, false)}
            </>
          ) : !hasValues ? (
            <>
              <Typography sx={{ fontSize: 14, color: t.t2, mb: 2.5 }}>
                למדד זה אין מדידות שמורות. הוא יוסתר מרשימת המדדים.
              </Typography>
              <Box sx={{ display: "flex", gap: 1.25 }}>
                {actionBtn("ביטול", t.t2, t.white, onClose, null, busy)}
                {actionBtn("הסתר מדד", "#fff", t.green, () => handle(false),
                  busy ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <HideIcon sx={{ fontSize: 18 }} />, busy)}
              </Box>
            </>
          ) : !confirmingForce ? (
            <>
              <Box sx={{
                display: "flex", alignItems: "flex-start", gap: 1, mb: 2,
                background: t.greenLight, borderRadius: t.rs, padding: "12px 14px",
              }}>
                <Typography sx={{ fontSize: 13.5, color: t.text, lineHeight: 1.5 }}>
                  למדד זה יש <b>{impact.valueCount}</b> מדידות שמורות אצל <b>{impact.traineeCount}</b> מתאמנים.
                  ניתן <b>להסתיר</b> את המדד (המדידות יישמרו לצפייה וניתן לשחזר אותו),
                  או <b>למחוק לצמיתות</b> את כל הנתונים.
                </Typography>
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {actionBtn("הסתר מדד (מומלץ — הנתונים יישמרו)", "#fff", t.green, () => handle(false),
                  busy ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <HideIcon sx={{ fontSize: 18 }} />, busy)}
                {actionBtn("מחק הכל לצמיתות", t.red, t.redLight, () => setConfirmingForce(true),
                  <ForceDeleteIcon sx={{ fontSize: 18 }} />, busy)}
                {actionBtn("ביטול", t.t2, t.white, onClose, null, busy)}
              </Box>
            </>
          ) : (
            <>
              <Box sx={{
                display: "flex", alignItems: "flex-start", gap: 1, mb: 2,
                background: t.redLight, borderRadius: t.rs, padding: "12px 14px",
              }}>
                <WarningIcon sx={{ fontSize: 20, color: t.red, mt: "1px" }} />
                <Typography sx={{ fontSize: 13.5, color: t.redText, lineHeight: 1.5, fontWeight: 500 }}>
                  פעולה זו תמחק לצמיתות את <b>{impact.valueCount}</b> המדידות אצל
                  <b> {impact.traineeCount}</b> מתאמנים. לא ניתן לשחזר.
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 1.25 }}>
                {actionBtn("חזרה", t.t2, t.white, () => setConfirmingForce(false), null, busy)}
                {actionBtn("מחק הכל לצמיתות", "#fff", t.red, () => handle(true),
                  busy ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <ForceDeleteIcon sx={{ fontSize: 18 }} />, busy)}
              </Box>
            </>
          )}
        </Box>
      </RTL>
    </Dialog>
  );
};

export default DeleteMeasurementDefinitionDialog;
